import type {
  PaginatedResponse,
  ProductCategory,
  ProductDetails,
  ProductListItem,
} from "../types/products";

// Mock categories
export const mockCategories: ProductCategory[] = [
  { id: "1", name: "Vitamins & Supplements", type: "CATEGORY" },
  { id: "2", name: "Weight Management", type: "CATEGORY" },
  { id: "3", name: "Energy & Performance", type: "CATEGORY" },
  { id: "4", name: "Digestive Health", type: "CATEGORY" },
  { id: "5", name: "Immune Support", type: "CATEGORY" },
];

// Mock health goals
export const mockHealthGoals: ProductCategory[] = [
  { id: "6", name: "Boost Energy", type: "HEALTH_GOAL" },
  { id: "7", name: "Healthy Fat Loss", type: "HEALTH_GOAL" },
  { id: "8", name: "Enhance Overall Health", type: "HEALTH_GOAL" },
  { id: "9", name: "Build Lean Muscle", type: "HEALTH_GOAL" },
  { id: "10", name: "Improve Digestion", type: "HEALTH_GOAL" },
];

// Helper to generate product variants
const generateVariants = (baseProduct: any, familyId: string) => {
  const sizes = ["30 capsules", "60 capsules", "90 capsules", "120 capsules"];
  const strengths = ["Regular", "Extra Strength", "Maximum"];

  return sizes.flatMap((size, sizeIndex) =>
    strengths.map((strength, strengthIndex) => ({
      id: `${familyId}-${sizeIndex}-${strengthIndex}`,
      name: `${strength} - ${size}`,
      sku: `${baseProduct.sku}-${strength.toUpperCase().replace(" ", "_")}-${size.split(" ")[0]}`,
      size,
      strength,
      price: {
        retail:
          baseProduct.basePrice *
          (1 + strengthIndex * 0.3) *
          (1 + sizeIndex * 0.25),
        preferred:
          baseProduct.basePrice *
          (1 + strengthIndex * 0.3) *
          (1 + sizeIndex * 0.25) *
          0.85,
        ibo:
          baseProduct.basePrice *
          (1 + strengthIndex * 0.3) *
          (1 + sizeIndex * 0.25) *
          0.7,
        currency: "USD",
        qv: Math.round(
          baseProduct.basePrice *
            (1 + strengthIndex * 0.3) *
            (1 + sizeIndex * 0.25) *
            0.8
        ),
      },
      inStock: Math.random() > 0.2,
    }))
  );
};

// Mock product data with variants
const mockProductsData = [
  {
    familyId: "fam-1",
    familyName: "VitaLife C",
    sku: "VLC",
    basePrice: 29.99,
    description:
      "Premium Vitamin C supplement for immune support and antioxidant protection",
    imageUrl:
      "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400",
    categories: [mockCategories[0], mockCategories[4]],
    healthGoals: [mockHealthGoals[2], mockHealthGoals[0]],
    ingredients: [
      { name: "Vitamin C (Ascorbic Acid)", imageUrl: null },
      { name: "Citrus Bioflavonoids", imageUrl: null },
      { name: "Rose Hips Extract", imageUrl: null },
    ],
    benefits: [
      { title: "Supports immune system function" },
      { title: "Powerful antioxidant protection" },
      { title: "Promotes collagen synthesis" },
    ],
    fdaDisclaimer:
      "These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.",
    packaging: "Bottle with child-resistant cap",
    dosage: {
      recommended: "Take 1-2 capsules daily with food",
      maximum: { amount: 4, unit: "capsules" },
      instructions: "Best taken with meals to enhance absorption",
      timing: "Morning and evening",
    },
  },
  {
    familyId: "fam-2",
    familyName: "MetaBoost Pro",
    sku: "MBP",
    basePrice: 39.99,
    description:
      "Advanced metabolic support formula for healthy weight management",
    imageUrl: "https://images.unsplash.com/photo-1556227834-09f1de7a7d14?w=400",
    categories: [mockCategories[1], mockCategories[2]],
    healthGoals: [mockHealthGoals[1], mockHealthGoals[3]],
    ingredients: [
      { name: "Green Tea Extract", imageUrl: null },
      { name: "Garcinia Cambogia", imageUrl: null },
      { name: "Chromium Picolinate", imageUrl: null },
      { name: "L-Carnitine", imageUrl: null },
    ],
    benefits: [
      { title: "Supports healthy metabolism" },
      { title: "Promotes fat burning" },
      { title: "Helps control appetite" },
      { title: "Increases energy levels" },
    ],
    fdaDisclaimer:
      "These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.",
    packaging: "Eco-friendly bottle with moisture-proof seal",
    dosage: {
      recommended: "Take 2 capsules 30 minutes before meals",
      maximum: { amount: 6, unit: "capsules" },
      instructions: "Do not exceed recommended dosage. Drink plenty of water.",
      timing: "Before breakfast and lunch",
    },
  },
  {
    familyId: "fam-3",
    familyName: "DigestiZen",
    sku: "DGZ",
    basePrice: 24.99,
    description:
      "Complete digestive enzyme complex for optimal nutrient absorption",
    imageUrl:
      "https://images.unsplash.com/photo-1609619385002-f40f6492eef6?w=400",
    categories: [mockCategories[3]],
    healthGoals: [mockHealthGoals[4], mockHealthGoals[2]],
    ingredients: [
      { name: "Probiotic Blend (10 Billion CFU)", imageUrl: null },
      { name: "Digestive Enzyme Complex", imageUrl: null },
      { name: "Ginger Root Extract", imageUrl: null },
      { name: "Peppermint Oil", imageUrl: null },
    ],
    benefits: [
      { title: "Improves digestion and nutrient absorption" },
      { title: "Reduces bloating and gas" },
      { title: "Supports gut health" },
      { title: "Enhances immune function" },
    ],
    fdaDisclaimer:
      "These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.",
    packaging: "Dark glass bottle to preserve probiotic potency",
    dosage: {
      recommended: "Take 1 capsule with each main meal",
      maximum: { amount: 3, unit: "capsules" },
      instructions: "Take with food for best results",
      timing: "With breakfast, lunch, and dinner",
    },
  },
  {
    familyId: "fam-4",
    familyName: "EnergyMax",
    sku: "EMX",
    basePrice: 34.99,
    description: "Natural energy booster with B-vitamins and adaptogens",
    imageUrl: "https://images.unsplash.com/photo-1556228720-da59c17165d3?w=400",
    categories: [mockCategories[2], mockCategories[0]],
    healthGoals: [mockHealthGoals[0], mockHealthGoals[3]],
    ingredients: [
      { name: "B-Complex Vitamins", imageUrl: null },
      { name: "Rhodiola Rosea", imageUrl: null },
      { name: "Ashwagandha", imageUrl: null },
      { name: "CoQ10", imageUrl: null },
      { name: "Caffeine (from Green Coffee Bean)", imageUrl: null },
    ],
    benefits: [
      { title: "Sustained energy without crashes" },
      { title: "Improves mental clarity and focus" },
      { title: "Reduces fatigue and stress" },
      { title: "Supports athletic performance" },
    ],
    fdaDisclaimer:
      "These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.",
    packaging: "Convenient travel-size bottle",
    dosage: {
      recommended: "Take 1-2 capsules in the morning",
      maximum: { amount: 3, unit: "capsules" },
      instructions: "Do not take within 6 hours of bedtime",
      timing: "Morning or early afternoon",
    },
  },
  {
    familyId: "fam-5",
    familyName: "OmegaPure 3",
    sku: "OP3",
    basePrice: 44.99,
    description: "Ultra-pure omega-3 fish oil for heart and brain health",
    imageUrl:
      "https://images.unsplash.com/photo-1607003203394-82eee77e4dc0?w=400",
    categories: [mockCategories[0]],
    healthGoals: [mockHealthGoals[2]],
    ingredients: [
      { name: "Fish Oil (1000mg)", imageUrl: null },
      { name: "EPA (360mg)", imageUrl: null },
      { name: "DHA (240mg)", imageUrl: null },
      { name: "Vitamin E", imageUrl: null },
    ],
    benefits: [
      { title: "Supports cardiovascular health" },
      { title: "Promotes brain function" },
      { title: "Reduces inflammation" },
      { title: "Supports joint health" },
    ],
    fdaDisclaimer:
      "These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.",
    packaging: "Soft gel capsules in dark bottle",
    dosage: {
      recommended: "Take 2 soft gels daily with meals",
      maximum: { amount: 4, unit: "soft gels" },
      instructions: "Store in a cool, dry place",
      timing: "With breakfast and dinner",
    },
  },
];

// Generate full product list with variants
export const mockProducts: ProductListItem[] = mockProductsData.flatMap(
  (product) => {
    const variants = generateVariants(product, product.familyId);
    return variants.map(
      (variant) =>
        ({
          id: variant.id,
          name: `${product.familyName} - ${variant.name}`,
          description: product.description,
          type: "supplement",
          benefits: product.benefits,
          categories: product.categories,
          isActive: variant.inStock,
          sku: variant.sku,
          imageUrl: product.imageUrl,
          // Additional fields for detailed view
          variant,
          familyId: product.familyId,
          familyName: product.familyName,
        }) as ProductListItem
    );
  }
);

// Generate detailed product info
export const generateProductDetails = (productId: string): ProductDetails => {
  const product = mockProducts.find((p) => p.id === productId);
  if (!product) {
    throw new Error("Product not found");
  }

  const baseProduct = mockProductsData.find(
    (p) => p.familyId === product.familyId
  );
  if (!baseProduct) {
    throw new Error("Base product not found");
  }

  return {
    ...product,
    ingredients: baseProduct.ingredients,
    healthGoals: baseProduct.healthGoals,
    compatibility: [],
    dosage: baseProduct.dosage,
    storage: "Store in a cool, dry place away from direct sunlight",
    nutritionalFacts: "See bottle for complete nutritional information",
    measurementUnit: "capsules",
    minimumRestockThreshold: "10",
    // Additional fields
    fdaDisclaimer: baseProduct.fdaDisclaimer,
    packaging: baseProduct.packaging,
    variants: generateVariants(baseProduct, baseProduct.familyId),
    price: product.price || (baseProduct as any)?.variants?.at(0)?.price,
  } as ProductDetails;
};

// Helper to filter products
export const filterProducts = (
  products: ProductListItem[],
  filters: {
    search?: string;
    categories?: string[];
    healthGoals?: string[];
  }
): ProductListItem[] => {
  let filtered = [...products];

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(
      (p) =>
        p.name.toLowerCase().includes(searchLower) ||
        p.description?.toLowerCase().includes(searchLower) ||
        p.sku.toLowerCase().includes(searchLower)
    );
  }

  if (filters.categories && filters.categories.length > 0) {
    filtered = filtered.filter((p) =>
      p.categories.some((c) => filters.categories!.includes(c.id))
    );
  }

  if (filters.healthGoals && filters.healthGoals.length > 0) {
    const baseProducts = mockProductsData.filter((bp) =>
      bp.healthGoals.some((hg) => filters.healthGoals!.includes(hg.id))
    );
    const familyIds = baseProducts.map((bp) => bp.familyId);
    filtered = filtered.filter(
      (p) => p.familyId && familyIds.includes(p.familyId)
    );
  }

  return filtered;
};

// Generate paginated response
export const paginateProducts = (
  products: ProductListItem[],
  page: number,
  perPage: number
): PaginatedResponse<ProductListItem> => {
  const start = (page - 1) * perPage;
  const end = start + perPage;
  const paginatedData = products.slice(start, end);

  return {
    data: paginatedData,
    pagination: {
      total: products.length,
      page,
      perPage,
      per_page: perPage,
      totalPages: Math.ceil(products.length / perPage),
      total_pages: Math.ceil(products.length / perPage),
    },
  };
};
